/**
 * ProductForm.jsx
 * 製品登録・編集フォーム専用コンポーネント
 * 基本単価機能（税抜き標準価格）
 * PWA対応: ボトムナビゲーションとの重複回避
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  CircularProgress,
  Divider,
  Chip,
  InputAdornment
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import { PRICE_CONSTANTS } from './priceValidationUtils';

// バリデーション定数
const MAX_NAME_LENGTH = 50;

const ProductForm = ({
  formData,
  errors,
  loading,
  isProductLimitReached,
  planLimits,
  onSubmit,
  onCancel,
  onNameChange,
  onUnitChange,
  onStandardUnitPriceChange
}) => {
  
  // 単位の選択肢
  const unitOptions = [
    { value: 'L', label: 'リットル (L)' },
    { value: 'kg', label: 'キログラム (kg)' },
    { value: 'g', label: 'グラム (g)' },
    { value: 'ml', label: 'ミリリットル (ml)' },
    { value: 'pcs', label: '個数 (個)' }
  ];

  return (
    <Box sx={{ 
      p: 3,
      // 🚀 PWA対応: ボトムナビゲーション用の安全マージンを追加
      pb: { xs: 8, sm: 3 } // モバイルは8（64px）、デスクトップは3（24px）
    }}>
      <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 500 }}>
        {isProductLimitReached && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label="製品登録上限に達しています"
              color="error"
              icon={<InfoIcon />}
              sx={{ fontWeight: 500 }}
            />
          </Box>
        )}
        {formData.id ? '製品情報を編集' : '新しい製品を登録'}
      </Typography>
      
      <form onSubmit={onSubmit}>
        <Grid container spacing={3}>
          {/* 製品名 */}
          <Grid item xs={12}>
            <TextField
              name="name"
              label="製品名"
              value={formData.name}
              onChange={onNameChange}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name || `${formData.name.length}/${MAX_NAME_LENGTH} 文字`}
              variant="outlined"
            />
          </Grid>

          {/* 単位 */}
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="unit-label">単位</InputLabel>
              <Select
                labelId="unit-label"
                name="unit"
                value={formData.unit}
                onChange={onUnitChange}
                label="単位"
                error={!!errors.unit}
              >
                {unitOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.unit && <FormHelperText error>{errors.unit}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* 標準価格フィールド（税抜き） */}
          <Grid item xs={12}>
            <TextField
              name="standardUnitPrice"
              label="標準価格（任意・税抜き）"
              type="text"
              value={formData.standardUnitPrice}
              onChange={onStandardUnitPriceChange}
              fullWidth
              error={!!errors.standardUnitPrice}
              helperText={
                errors.standardUnitPrice || 
                `空欄可：設定すると顧客画面で価格が表示されます（小数点以下${PRICE_CONSTANTS.SCALE}桁まで）`
              }
              variant="outlined"
              InputProps={{
                startAdornment: <InputAdornment position="start">¥</InputAdornment>,
              }}
              placeholder={`例: 1200.50（0〜${PRICE_CONSTANTS.MAX_VALUE.toLocaleString()}）`}
            />
          </Grid>

          {/* ボタン */}
          <Grid item xs={12} sx={{ mt: 2, textAlign: 'center' }}>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={onCancel}
                disabled={loading}
              >
                キャンセル
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={
                  loading || 
                  !!errors.name || 
                  !!errors.standardUnitPrice || 
                  !formData.name || 
                  (!formData.id && isProductLimitReached)
                }
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? '処理中...' : formData.id ? '製品を更新する' : '製品を登録する'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* 標準価格に関する注意書き */}
      <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(25, 118, 210, 0.05)', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>標準価格について：</strong>
        </Typography>
        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem', color: '#666' }}>
          <li>顧客ごとに個別価格が設定されていない場合、この標準価格が適用されます</li>
          <li>顧客個別価格が設定されている場合は、そちらが優先されます</li>
          <li>小数点以下{PRICE_CONSTANTS.SCALE}桁まで設定可能です</li>
          <li>現在は税抜き価格のみ対応しています</li>
        </ul>
      </Box>

      {/* プラン制限情報 */}
      {formData.id ? (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            製品情報を変更すると、関連する全ての記録にも反映されます。
          </Typography>
        </Box>
      ) : (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          bgcolor: isProductLimitReached ? 'rgba(244, 67, 54, 0.05)' : 'rgba(25, 118, 210, 0.05)', 
          borderRadius: 1 
        }}>
          <Typography 
            variant="body2" 
            color={isProductLimitReached ? "error" : "text.secondary"}
          >
            {isProductLimitReached ? (
              <>
                <InfoIcon fontSize="small" color="error" sx={{ mr: 1, verticalAlign: 'middle' }} />
                製品登録上限（{planLimits.maxProducts}件）に達しています。
              </>
            ) : (
              <>現在 {planLimits.currentProducts}/{planLimits.maxProducts} 件の製品が登録されています。</>
            )}
          </Typography>
        </Box>
      )}

      {/* 🚀 PWA対応: 追加の安全マージン（フォーム最下部） */}
      <Box sx={{ 
        height: { xs: 60, sm: 20 }, // モバイル60px、デスクトップ20px
        width: '100%'
      }} />
    </Box>
  );
};

export default ProductForm;